import type { DocPackCatalogPage, DocPackPage } from '@listening/doc-pack';
import type { DocPackPlaybackAnchor } from '../audio-chunk-player/core';
import {
  catalogPageTotalChars,
  docPackCatalogPageReadableCounts,
  docPackPageReadableCharOffsetForAnchor,
  type DocPackReadingClassOverrides,
} from './reading-plan';
import { docPackCharRangesOverlap, type DocPackTextPart } from './text-parts';
import { readalongFontConfig } from '../stores/audio-preferences-store';
import { useState } from 'react';

export type ReadAlongFontSize = (typeof readalongFontConfig.fontSizes)[number];
export type ReadAlongFontStyle =
  (typeof readalongFontConfig.fontStyles)[number];
export type ReadAlongFontWeight =
  (typeof readalongFontConfig.fontWeights)[number];
export type ReadAlongLineHeight =
  (typeof readalongFontConfig.lineHeights)[number];
export type ReadAlongFontWeightValue =
  (typeof readalongFontConfig.fontWeightLabelToValue)[ReadAlongFontWeight];

export const DOC_PACK_FONT_SIZE_PX: Record<ReadAlongFontSize, number> = {
  Small: 16,
  Medium: 18,
  Large: 20,
};
export const DOC_PACK_FONT_FAMILY: Record<ReadAlongFontStyle, string> = {
  Poppins: 'Poppins',
  Besley: 'Besley',
  'Roboto Serif': 'Roboto Serif',
  'Playfair Display': 'Playfair Display',
  Quicksand: 'Quicksand',
  OpenDyslexic: 'OpenDyslexic',
};
export const DOC_PACK_LINE_HEIGHT_MULTIPLIER =
  readalongFontConfig.lineHeightLabelToMultiplier.Comfort;
export const DOC_PACK_LINE_HEIGHT_MULTIPLIERS =
  readalongFontConfig.lineHeightLabelToMultiplier;
export const DOC_PACK_ROW_HORIZONTAL_PADDING = 48;
export const DOC_PACK_ROW_VERTICAL_PADDING = 32;
const DOC_PACK_ROW_VERTICAL_PADDING_TOP = DOC_PACK_ROW_VERTICAL_PADDING / 2;
export const DOC_PACK_AVG_CHAR_WIDTH_RATIO = 0.5;
export const DOC_PACK_FALLBACK_PLACEHOLDER_LINES = 7;
/** Vertical padding of every reading range block (top + bottom). */
export const DOC_PACK_BLOCK_VERTICAL_PADDING = 20;
const DOC_PACK_HEADING_FONT_SIZE_STEP = 7;
const DOC_PACK_HEADING_LINE_HEIGHT_RATIO = 1.3;
// Fitted to rendered pages on Android (Alice in Wonderland and a Wikipedia
// article, 2026-10): the catalog has no block count, and pages average about
// 90 readable chars per block; word wrapping leaves lines about 94% full.
const DOC_PACK_ESTIMATED_BLOCK_CHARS = 90;
const DOC_PACK_LINE_FILL = 0.94;

export type DocPackTextMetrics = {
  fontSize: number;
  lineHeight: number;
  charsPerLine: number;
  fontFamily: string;
  fontWeight: ReadAlongFontWeightValue;
};

export function deriveDocPackTextMetrics(
  size: ReadAlongFontSize,
  width: number,
  style: ReadAlongFontStyle = 'Poppins',
  weight: ReadAlongFontWeight = 'Regular',
  lineHeightPreference: ReadAlongLineHeight = 'Comfort',
): DocPackTextMetrics {
  const fontSize = DOC_PACK_FONT_SIZE_PX[size];
  const lineHeight = Math.round(
    fontSize * DOC_PACK_LINE_HEIGHT_MULTIPLIERS[lineHeightPreference],
  );
  const usableWidth = Math.max(1, width - DOC_PACK_ROW_HORIZONTAL_PADDING);
  const charsPerLine = Math.max(
    1,
    Math.floor(usableWidth / (fontSize * DOC_PACK_AVG_CHAR_WIDTH_RATIO)),
  );
  return {
    fontSize,
    lineHeight,
    charsPerLine,
    fontFamily: DOC_PACK_FONT_FAMILY[style],
    fontWeight: readalongFontConfig.fontWeightLabelToValue[weight],
  };
}

export function docPackHeadingMetrics(metrics: DocPackTextMetrics) {
  const fontSize = metrics.fontSize + DOC_PACK_HEADING_FONT_SIZE_STEP;
  return {
    fontSize,
    lineHeight: Math.round(fontSize * DOC_PACK_HEADING_LINE_HEIGHT_RATIO),
    charsPerLine: Math.max(
      1,
      Math.floor((metrics.charsPerLine * metrics.fontSize) / fontSize),
    ),
  };
}

const estimateBlocksHeight = (
  chars: number,
  charsPerLine: number,
  lineHeight: number,
) => {
  if (chars <= 0) return 0;
  const blocks = Math.max(
    1,
    Math.round(chars / DOC_PACK_ESTIMATED_BLOCK_CHARS),
  );
  // Each block's last line is half full on average.
  const lines = chars / (charsPerLine * DOC_PACK_LINE_FILL) + blocks / 2;
  return blocks * DOC_PACK_BLOCK_VERTICAL_PADDING + lines * lineHeight;
};

export function estimateDocPackPageRowHeight(
  catalogPage: DocPackCatalogPage | undefined,
  overrides: DocPackReadingClassOverrides,
  metrics: DocPackTextMetrics,
): number {
  if (!catalogPage) {
    return (
      metrics.lineHeight * DOC_PACK_FALLBACK_PLACEHOLDER_LINES +
      DOC_PACK_ROW_VERTICAL_PADDING
    );
  }
  const { chars } = docPackCatalogPageReadableCounts(catalogPage, overrides);
  const headingCounts = catalogPage.classes.heading;
  const headingChars = headingCounts
    ? docPackCatalogPageReadableCounts(
        { ...catalogPage, classes: { heading: headingCounts } },
        overrides,
      ).chars
    : 0;
  const heading = docPackHeadingMetrics(metrics);
  return Math.round(
    DOC_PACK_ROW_VERTICAL_PADDING +
      Math.max(
        metrics.lineHeight,
        estimateBlocksHeight(
          chars - headingChars,
          metrics.charsPerLine,
          metrics.lineHeight,
        ) +
          estimateBlocksHeight(
            headingChars,
            heading.charsPerLine,
            heading.lineHeight,
          ),
      ),
  );
}

export type DocPackRenderedWordOffset = {
  readonly pageStart: number;
  readonly pageEnd: number;
  readonly renderedStart: number;
};

export function buildDocPackRenderedWordOffsets(
  parts: readonly DocPackTextPart[],
): readonly DocPackRenderedWordOffset[] {
  const offsets: DocPackRenderedWordOffset[] = [];
  let rendered = 0;
  for (const part of parts) {
    if (part.type === 'text') {
      rendered += part.text.length;
      continue;
    }
    offsets.push({
      pageStart: part.word.chars[0],
      pageEnd: part.word.chars[1],
      renderedStart: rendered,
    });
    rendered += part.word.text.length;
  }
  return offsets;
}

export type DocPackMeasuredLine = {
  readonly y: number;
  readonly renderedStart: number;
  readonly renderedEnd: number;
};

export function buildDocPackMeasuredLines(
  lines: readonly { readonly y: number; readonly text: string }[],
): readonly DocPackMeasuredLine[] {
  let rendered = 0;
  return lines.map((line) => {
    const renderedStart = rendered;
    rendered += line.text.length;
    return { y: line.y, renderedStart, renderedEnd: rendered };
  });
}

export type DocPackRangeTextMeasurement = {
  readonly chars: readonly [number, number];
  readonly lines: readonly DocPackMeasuredLine[];
  readonly words: readonly DocPackRenderedWordOffset[];
};

const LINE_TEXT_COVERAGE_THRESHOLD = 0.6;

export function docPackFollowActiveLineContentY({
  scrollY,
  elementWindowY,
  containerWindowY,
  lineTop,
}: {
  scrollY: number;
  elementWindowY: number;
  containerWindowY: number;
  lineTop: number;
}): number {
  return scrollY + (elementWindowY + lineTop - containerWindowY);
}

export function docPackFollowRestTargetY({
  scrollY,
  elementWindowY,
  containerWindowY,
  lineTop,
  viewportHeight,
  contentHeight,
  restFraction,
}: {
  scrollY: number;
  elementWindowY: number;
  containerWindowY: number;
  lineTop: number;
  viewportHeight: number;
  contentHeight: number;
  restFraction: number;
}): number {
  const maxScroll = Math.max(0, contentHeight - viewportHeight);
  const rest = viewportHeight * restFraction;
  return Math.min(
    maxScroll,
    Math.max(
      0,
      docPackFollowActiveLineContentY({
        scrollY,
        elementWindowY,
        containerWindowY,
        lineTop,
      }) - rest,
    ),
  );
}

export function isContentLineVisible({
  lineContentY,
  lineHeight,
  scrollY,
  viewportHeight,
}: {
  lineContentY: number;
  lineHeight: number;
  scrollY: number;
  viewportHeight: number;
}): boolean {
  return (
    viewportHeight > 0 &&
    lineContentY + lineHeight >= scrollY &&
    lineContentY <= scrollY + viewportHeight
  );
}

export function docPackAnchorLineTop(
  measurement: DocPackRangeTextMeasurement,
  anchorCharStart: number,
): number {
  const { lines, words } = measurement;
  if (lines.length === 0 || words.length === 0) return 0;
  const word =
    words.find(
      (w) => anchorCharStart >= w.pageStart && anchorCharStart < w.pageEnd,
    ) ?? words.find((w) => w.pageStart >= anchorCharStart);
  if (!word) return 0;
  const lastWord = words.at(-1);
  const expectedRendered = lastWord
    ? lastWord.renderedStart + (lastWord.pageEnd - lastWord.pageStart)
    : 0;
  const reportedRendered = lines.at(-1)?.renderedEnd ?? 0;
  const linesReliable =
    expectedRendered > 0 &&
    reportedRendered >= expectedRendered * LINE_TEXT_COVERAGE_THRESHOLD;
  if (linesReliable) {
    const exact = lines.find(
      (l) =>
        l.renderedEnd > l.renderedStart &&
        word.renderedStart >= l.renderedStart &&
        word.renderedStart < l.renderedEnd,
    );
    if (exact) return exact.y;
  }
  const total = Math.max(expectedRendered, reportedRendered);
  if (total <= 0) return 0;
  const index = Math.min(
    lines.length - 1,
    Math.max(0, Math.floor((word.renderedStart / total) * lines.length)),
  );
  return lines[index]?.y ?? 0;
}

export function docPackTextAnchorViewOffset({
  anchor,
  catalogPage,
  metrics,
  page,
  topMargin,
  overrides,
}: {
  readonly anchor: DocPackPlaybackAnchor;
  readonly catalogPage: DocPackCatalogPage | undefined;
  readonly metrics: DocPackTextMetrics;
  readonly page: DocPackPage | undefined;
  readonly topMargin: number;
  readonly overrides?: DocPackReadingClassOverrides;
}): number | null {
  let readableOffset: number;
  if (page) {
    readableOffset = docPackPageReadableCharOffsetForAnchor(
      page,
      anchor,
      overrides,
    );
  } else {
    if (!catalogPage) return null;
    const counts = docPackCatalogPageReadableCounts(catalogPage, overrides);
    const totalChars = catalogPageTotalChars(catalogPage);
    if (counts.chars === 0 || totalChars === 0) return null;
    const ratio = Math.min(1, Math.max(0, anchor.charStart / totalChars));
    readableOffset = counts.chars * ratio;
  }

  const lineIndex = Math.max(
    0,
    Math.floor(readableOffset / metrics.charsPerLine),
  );
  const offsetInsideRow =
    DOC_PACK_ROW_VERTICAL_PADDING_TOP + lineIndex * metrics.lineHeight;
  return offsetInsideRow - topMargin;
}

export function scopeAnchorToPage(
  anchor: DocPackPlaybackAnchor | undefined,
  pageNumber: number,
): DocPackPlaybackAnchor | undefined {
  return anchor?.page === pageNumber ? anchor : undefined;
}

export function docPackAnchorsEqual(
  a: DocPackPlaybackAnchor | undefined,
  b: DocPackPlaybackAnchor | undefined,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return (
    a.page === b.page && a.charStart === b.charStart && a.charEnd === b.charEnd
  );
}

export function useStableDocPackAnchor(
  anchor: DocPackPlaybackAnchor | undefined,
): DocPackPlaybackAnchor | undefined {
  const [stable, setStable] = useState(anchor);
  if (docPackAnchorsEqual(stable, anchor)) return stable;
  setStable(anchor);
  return anchor;
}

export function isDocPackOverlayWordActive(
  wordChars: readonly [number, number],
  scopedAnchor: DocPackPlaybackAnchor | undefined,
): boolean {
  return (
    scopedAnchor !== undefined &&
    docPackCharRangesOverlap(wordChars, [
      scopedAnchor.charStart,
      scopedAnchor.charEnd,
    ])
  );
}

export function isDocPackPartActive(
  part: DocPackTextPart,
  anchor: DocPackPlaybackAnchor | undefined,
  pageNumber: number,
): boolean {
  return (
    part.type === 'word' &&
    anchor?.page === pageNumber &&
    docPackCharRangesOverlap(part.word.chars, [
      anchor.charStart,
      anchor.charEnd,
    ])
  );
}
